import React from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router';
import { analytics } from '../../utils/analytics';
import { havenColors } from '../../theme/theme';
import homeownerImage from '../../assets/images/Homeowner-button.webp';
import homeownerImageFallback from '../../assets/images/Homeowner-button.png';
import hvacProImage from '../../assets/images/home_hvac-pro.webp';
import hvacProImageFallback from '../../assets/images/home_hvac-pro.png';

const HomeAudienceSplit: React.FC = () => {
  return (
    <Box
      sx={{
        borderRadius: '32px',
        p: { xs: 3, md: 4 },
        background: 'linear-gradient(180deg, rgba(247,251,253,1) 0%, rgba(255,255,255,1) 100%)',
        border: '1px solid rgba(155, 179, 191, 0.18)',
      }}
    >
      <Grid container spacing={4} sx={{ mb: 3, alignItems: 'end' }}>
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', mb: 1, color: 'text.secondary', letterSpacing: '0.12em' }}
          >
            Choose your path
          </Typography>
          <Typography variant="h3" sx={{ mb: 1.25, maxWidth: 520 }}>
            One platform, two paths.
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              borderRadius: '28px',
              backgroundColor: '#fff',
              border: '1px solid rgba(155, 179, 191, 0.18)',
              boxShadow: '0 18px 36px rgba(83, 95, 102, 0.08)',
            }}
          >
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              <Box
                component="picture"
                sx={{
                  display: 'block',
                  flexShrink: 0,
                  width: { xs: '100%', md: 200 },
                  borderRadius: '22px',
                  overflow: 'hidden',
                  background: 'linear-gradient(180deg, #f7f3ea 0%, #f3f7f8 100%)',
                  boxShadow: '0 18px 24px rgba(83, 95, 102, 0.12)',
                }}
              >
                <source srcSet={homeownerImage} type="image/webp" />
                <Box
                  component="img"
                  src={homeownerImageFallback}
                  alt="Homeowners"
                  sx={{
                    width: '100%',
                    height: { xs: 260, md: 280 },
                    objectFit: 'cover',
                    objectPosition: 'top center',
                    display: 'block'
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" sx={{ display: 'block', color: havenColors.secondaryGrey, letterSpacing: '0.12em', mb: 1 }}>
                  Homeowners
                </Typography>
                <Typography variant="h4" sx={{ mb: 1.25 }}>
                  See what your home is telling you.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 420 }}>
                  Get a clearer picture of what is happening at home and what to do next.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to="/homeowner-start"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => analytics.trackSegmentSelection('homeowner', 'home_audience_cards', '/homeowner-start')}
                  >
                    For Homeowners
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/homeowner-start"
                    variant="text"
                    endIcon={<ArrowForward />}
                    sx={{ whiteSpace: 'nowrap' }}
                    aria-label="Learn more about HAVEN for homeowners"
                  >
                    Learn More
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              borderRadius: '28px',
              backgroundColor: '#fff',
              border: '1px solid rgba(155, 179, 191, 0.18)',
              boxShadow: '0 18px 36px rgba(83, 95, 102, 0.08)',
            }}
          >
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              <Box
                component="picture"
                sx={{
                  display: 'block',
                  flexShrink: 0,
                  width: { xs: '100%', md: 200 },
                  borderRadius: '22px',
                  overflow: 'hidden',
                  background: 'linear-gradient(180deg, #f6f2e8 0%, #f4f7f8 100%)',
                  boxShadow: '0 18px 24px rgba(83, 95, 102, 0.12)',
                }}
              >
                <source srcSet={hvacProImage} type="image/webp" />
                <Box
                  component="img"
                  src={hvacProImageFallback}
                  alt="HVAC pros"
                  sx={{
                    width: '100%',
                    height: { xs: 260, md: 280 },
                    objectFit: 'cover',
                    objectPosition: 'top center',
                    display: 'block'
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" sx={{ display: 'block', color: havenColors.secondaryGrey, letterSpacing: '0.12em', mb: 1 }}>
                  HVAC Pros
                </Typography>
                <Typography variant="h4" sx={{ mb: 1.25 }}>
                  Fix IAQ issues for customers.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 420 }}>
                  Build a better service model with clearer visibility, response, and proof.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to="/become-a-pro"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => analytics.trackSegmentSelection('hvac_pro', 'home_audience_cards', '/become-a-pro')}
                  >
                    For Pros
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/become-a-pro"
                    variant="text"
                    endIcon={<ArrowForward />}
                    sx={{ whiteSpace: 'nowrap' }}
                    aria-label="Learn more about HAVEN for HVAC pros"
                  >
                    Learn More
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomeAudienceSplit;
